"use client";
import SectionTitle from "@/shared/app/ui/text/PageSectionTitle";
import { motion } from "framer-motion";
import Image from "next/image";

interface SafePathCardProps {
  title: string;
  description: string;
  bgColor: string;
  images: string[];
  delay?: number;
  labelColor: string;
}

const SafePathCard = ({
  title,
  description,
  bgColor,
  images,
  delay = 0,
  labelColor,
}: SafePathCardProps) => {
  const gradient = `linear-gradient(to bottom, ${bgColor} 0%, #16234233 100%)`;

  return (
    <motion.div
      className={`flex flex-col grow rounded-3xl py-5  md:py-10 px-5 md:px-8 text-white overflow-hidden`}
      style={{ background: gradient }}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay }}
      viewport={{ once: true, amount: 0.3 }}
    >
      <motion.p
        className="text-[#E6FAFF] text-[15px] md:text-xl tracking-tight inline-flex justify-center items-center w-fit m-auto px-3 py-1 md:py-2 rounded-2xl"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: delay + 0.2 }}
        viewport={{ once: true }}
        style={{
          fontFamily: '"pre-SemiBold", sans-serif',
          backgroundColor: labelColor,
        }}
      >
        {title}
      </motion.p>

      <motion.p
        className="text-[#E6FAFF]  mt-2 text-sm md:text-lg leading-relaxed opacity-90 text-center tracking-tight"
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: delay + 0.3 }}
        viewport={{ once: true }}
        style={{
          fontFamily: '"pre-Medium", sans-serif',
        }}
      >
        {description}
      </motion.p>

      <div className="flex space-x-3 mt-4 md:mt-6">
        {images.map((src, idx) => (
          <motion.div
            key={idx}
            className="relative flex grow overflow-hidden rounded-xl md:rounded-2xl aspect-[4/3.5]"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: delay + 0.4 + idx * 0.2 }}
            viewport={{ once: true }}
          >
            <Image
              src={src}
              alt={`이미지 ${idx + 1}`}
              fill
              className="object-cover w-full h-full"
              sizes="(max-width: 768px) 100vw, 400px"
            />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default function MovementCollectionSection() {
  return (
    <div className="bg-[#181919] mt-40">
      <div className="w-full xl:max-w-[1280px] mx-auto px-5 xl:px-2 pt-30 md:pt-60 pb-20 flex flex-col">
        <div className="flex items-start xl:items-center justify-between w-full space-x-0 xl:space-x-20 xl:flex-row flex-col">
          {/* 왼쪽 텍스트 영역 */}
          <div className="mb-5 mr-0 lg:mr-16 lg:mb-0">
            <SectionTitle
              align="left"
              label="Data Collection App"
              title={
                <>
                  별도의 앱으로 <br />더 효율적인 정보 수집
                </>
              }
              description={
                <>
                  GPS 기반 경로 수집과 길 편의성 평가를 통해,
                  <br />
                  이동 정보를 정확하고 효율적으로 확보합니다.
                </>
              }
              colorMode="dark"
            />
          </div>
          {/* 오른쪽 이미지 영역 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.8,
              ease: "easeOut",
              delay: 0.2,
            }}
            viewport={{ once: true }}
            className="flex grow bg-white rounded-4xl pb-10 justify-center mt-10 xl:mt-0 w-full xl:w-auto overflow-hidden"
          >
            {/* ✅ 반응형 이미지 비율 유지 */}
            <div className="relative w-full xl:w-[614px] aspect-[1/1] max-w-[614px]">
              <Image
                src="/assets/images/page/service/movement/collectionApp.png"
                alt="앱스크린"
                fill
                className="object-contain"
                sizes="(max-width: 1280px) 100vw, 614px"
                priority={false}
              />
            </div>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="mt-6 md:mt-10 rounded-4xl py-10 md:px-8 px-5"
          style={{
            background:
              "linear-gradient(to bottom, #64AECD 0%, #54A496 63%, #4B4B8D 100%)",
          }}
        >
          <div className="flex flex-col justify-center items-center">
            {/* 아이콘 */}
            <motion.div
              className="relative w-6 h-6 md:w-11 md:h-11 overflow-hidden"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <Image
                src="/assets/images/page/service/movement/checkIcon.svg"
                alt="메인페이지"
                fill
                className="object-contain"
                sizes="(max-width: 1280px) 100vw, 614px"
                priority={false}
              />
            </motion.div>
            {/* 타이틀 */}
            <motion.p
              className="tracking-tight text-white md:text-4xl text-xl md:mt-8 mt-5 "
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
              viewport={{ once: true, amount: 0.3 }}
              style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
            >
              길 위의 안전을 3단계로 기록합니다
            </motion.p>

            {/* 설명문 */}
            <motion.p
              className="mt-4 md:mt-6 text-sm md:text-xl leading-relaxed md:leading-8 text-white/90 tracking-tight text-center break-keep"
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.5 }}
              viewport={{ once: true, amount: 0.3 }}
              style={{ fontFamily: '"pre-Regular", sans-serif' }}
            >
              앱을 통해 도로를 걸으며 휠체어 이용자의 이동 안전을 기록합니다.
              <br />
              안전·주의·위험의 세 단계로 나누어, 편의시설과 이동 경로의 상태를
              체계적으로 수집합니다.
            </motion.p>
          </div>

          <div className="flex space-x-0 lg:space-x-4 mt-10 md:mt-14 lg:flex-row flex-col lg:space-y-0 space-y-5">
            <SafePathCard
              title="안전한 길"
              description="평평하고 이동에 불편함이 없는 길"
              bgColor="#2C8933"
              labelColor="#459949"
              images={[
                "/assets/images/page/service/movement/road1-1.png",
                "/assets/images/page/service/movement/road1-2.png",
              ]}
            />
            <SafePathCard
              title="휠체어 이용 주의 길"
              description="약간의 울퉁불퉁함이나 경사가 있는 길"
              bgColor="#FF9B1C"
              labelColor="#F5961E"
              images={[
                "/assets/images/page/service/movement/road2-1.png",
                "/assets/images/page/service/movement/road2-2.png",
              ]}
              delay={0.2}
            />
            <SafePathCard
              title="휠체어 이용이 어려운 길"
              description="오르막, 계단 등 이동이 어려운 길"
              bgColor="#D44040"
              labelColor="#CF3D40"
              images={[
                "/assets/images/page/service/movement/road3-1.png",
                "/assets/images/page/service/movement/road3-2.png",
              ]}
              delay={0.4}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
